import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { jsPDF } from 'jspdf';
import { toast } from 'react-toastify';

const PrintPreviewModal = ({
  show,
  handleClose,
  socio,
  vistaPreviaFoto,
  qrUrl,
  ciudad,
  estadoTexto,
  gradoTexto,
  universidad,
  nroTitulo,
}) => {
  const [pdfUrl, setPdfUrl] = useState(null);

  const nombreCompleto = [socio?.nombres, socio?.apPat, socio?.apMat].filter(Boolean).join(' ');

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString('es-BO');
  };

  const imageToDataUrl = async (src) => {
    if (!src) return null;
    if (src.startsWith('data:')) return src;

    const response = await fetch(src);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const addHeader = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();

    try {
      doc.addImage('/assets/cabeceraciab.png', 'PNG', 52, 44, pageWidth - 104, 92);
    } catch (error) {
      doc.setFont('times', 'bold');
      doc.setFontSize(14);
      doc.text('COLEGIO DE INGENIEROS AGRONOMOS DE BOLIVIA - FILIAL TARIJA', pageWidth / 2, 74, { align: 'center' });
    }

    doc.setDrawColor(18, 72, 48);
    doc.setLineWidth(1.2);
    doc.line(52, 148, pageWidth - 52, 148);

    doc.setFont('times', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(18, 48, 31);
    doc.text('FICHA DEL ASOCIADO', pageWidth / 2, 170, { align: 'center' });

    doc.setFont('times', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(95, 111, 102);
    doc.text(`Emitido: ${new Date().toLocaleString('es-BO')}`, pageWidth - 52, 188, { align: 'right' });
  };

  const addFooter = (doc) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setDrawColor(18, 72, 48);
    doc.setLineWidth(0.8);
    doc.line(52, pageHeight - 72, pageWidth - 52, pageHeight - 72);

    doc.setFont('times', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(18, 72, 48);
    doc.text('Telf. Cel. 77171320; e-mail: ciabtarija@gmail.com', pageWidth / 2, pageHeight - 48, { align: 'center' });
    doc.text('Tarija - Bolivia', pageWidth / 2, pageHeight - 34, { align: 'center' });
  };

  const addField = (doc, label, value, x, y, width) => {
    doc.setFont('times', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(88, 104, 94);
    doc.text(label.toUpperCase(), x, y);

    doc.setFont('times', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(18, 35, 26);
    const lines = doc.splitTextToSize(String(value || 'N/A'), width);
    doc.text(lines, x, y + 15);
    return y + 15 + (lines.length * 13);
  };

  const generatePdf = useCallback(async () => {
    const doc = new jsPDF('p', 'pt', 'letter');
    const pageWidth = doc.internal.pageSize.getWidth();

    addHeader(doc);

    doc.setFillColor(248, 251, 249);
    doc.roundedRect(52, 204, pageWidth - 104, 430, 8, 8, 'F');
    doc.setDrawColor(214, 224, 218);
    doc.roundedRect(52, 204, pageWidth - 104, 430, 8, 8, 'S');

    doc.setFillColor(18, 72, 48);
    doc.roundedRect(52, 204, pageWidth - 104, 42, 8, 8, 'F');
    doc.setFont('times', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text(nombreCompleto || 'SIN NOMBRE REGISTRADO', 72, 230);

    doc.setFontSize(10);
    doc.text(estadoTexto || 'N/A', pageWidth - 72, 230, { align: 'right' });

    const photoX = 72;
    const photoY = 266;
    const photoW = 118;
    const photoH = 148;

    doc.setFillColor(232, 239, 234);
    doc.roundedRect(photoX, photoY, photoW, photoH, 8, 8, 'F');

    const photoData = await imageToDataUrl(vistaPreviaFoto).catch(() => null);
    if (photoData) {
      try {
        doc.addImage(photoData, undefined, photoX, photoY, photoW, photoH);
      } catch (error) {
        doc.setFont('times', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(115, 115, 115);
        doc.text('Sin foto', photoX + photoW / 2, photoY + photoH / 2, { align: 'center' });
      }
    }

    doc.setDrawColor(18, 72, 48);
    doc.setLineWidth(2);
    doc.roundedRect(photoX, photoY, photoW, photoH, 8, 8, 'S');

    const qrSize = 86;
    const qrX = 88;
    const qrY = 438;
    if (qrUrl) {
      doc.addImage(qrUrl, 'PNG', qrX, qrY, qrSize, qrSize);
    }
    doc.setFont('times', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(18, 72, 48);
    doc.text('VERIFICACION QR', qrX + qrSize / 2, qrY + qrSize + 13, { align: 'center' });

    const leftX = 214;
    const rightX = 392;
    const colW = 150;
    let y = 278;

    y = addField(doc, 'Registro profesional', socio?.codreg, leftX, y, colW);
    y = addField(doc, 'Cedula de identidad', socio?.ci, leftX, y + 10, colW);
    y = addField(doc, 'Fecha de registro', formatDate(socio?.fereg), leftX, y + 10, colW);
    addField(doc, 'Ciudad', ciudad || socio?.ciudad, leftX, y + 10, colW);

    let y2 = 278;
    y2 = addField(doc, 'Grado', gradoTexto, rightX, y2, colW);
    y2 = addField(doc, 'Nro. titulo', nroTitulo, rightX, y2 + 10, colW);
    y2 = addField(doc, 'Universidad asociada', universidad, rightX, y2 + 10, colW);
    addField(doc, 'Estado', estadoTexto, rightX, y2 + 10, colW);

    doc.setFillColor(236, 244, 239);
    doc.roundedRect(214, 548, pageWidth - 286, 48, 6, 6, 'F');
    doc.setFont('times', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(18, 72, 48);
    doc.text('Observacion institucional', 230, 568);
    doc.setFont('times', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(55, 65, 58);
    const observacion = doc.splitTextToSize(
      'Documento interno de consulta del asociado. La validez publica se verifica mediante el codigo QR.',
      pageWidth - 318
    );
    doc.text(observacion, 230, 584);

    addFooter(doc);

    const blob = doc.output('blob');
    return URL.createObjectURL(blob);
  }, [ciudad, estadoTexto, gradoTexto, nombreCompleto, nroTitulo, qrUrl, socio, universidad, vistaPreviaFoto]);

  useEffect(() => {
    if (!show || !socio) return undefined;

    let objectUrl;

    generatePdf()
      .then((url) => {
        objectUrl = url;
        setPdfUrl(url);
      })
      .catch((error) => {
        toast.error('No se pudo generar la vista previa de la ficha');
        console.error('Error generando ficha del socio:', error);
      });

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setPdfUrl(null);
    };
  }, [show, socio, generatePdf]);

  const printPdf = () => {
    if (!pdfUrl) return;
    const printWindow = window.open(pdfUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => printWindow.print();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Vista previa de ficha del asociado</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {pdfUrl ? (
          <iframe src={pdfUrl} width="100%" height="620px" title="Vista previa de ficha del asociado" />
        ) : (
          <div className="text-center py-5">Generando vista previa...</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cerrar
        </Button>
        <Button variant="primary" onClick={printPdf} disabled={!pdfUrl}>
          Imprimir
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PrintPreviewModal;
